import { ComponentType } from 'react';
import { PluginRoute, RouteRegistrationOptions } from './types';

/**
 * Utility functions for plugin route registration
 */

/**
 * Validates a plugin route configuration
 */
export const validateRoute = (route: PluginRoute): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!route.path) {
    errors.push('Route path is required');
  } else if (typeof route.path !== 'string') {
    errors.push('Route path must be a string');
  } else if (!route.path.startsWith('/')) {
    errors.push('Route path must start with "/"');
  }

  if (!route.component) {
    errors.push('Route component is required');
  } else if (typeof route.component !== 'function') {
    errors.push('Route component must be a React component');
  }

  if (route.name && typeof route.name !== 'string') {
    errors.push('Route name must be a string');
  }

  if (route.title && typeof route.title !== 'string') {
    errors.push('Route title must be a string');
  }

  if (route.order && typeof route.order !== 'number') {
    errors.push('Route order must be a number');
  }

  if (route.guard && typeof route.guard !== 'function') {
    errors.push('Route guard must be a function');
  }

  if (route.children) {
    if (!Array.isArray(route.children)) {
      errors.push('Route children must be an array');
    } else {
      route.children.forEach((child, index) => {
        const childValidation = validateRoute(child);
        if (!childValidation.valid) {
          errors.push(`Child route ${index}: ${childValidation.errors.join(', ')}`);
        }
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Creates a route configuration object with validation
 */
export const createRoute = (
  path: string,
  component: ComponentType<any>,
  options: Partial<Omit<PluginRoute, 'path' | 'component'>> = {}
): PluginRoute => {
  const route: PluginRoute = {
    path,
    component,
    ...options
  };

  const validation = validateRoute(route);
  if (!validation.valid) {
    throw new Error(`Invalid route configuration: ${validation.errors.join(', ')}`);
  }

  return route;
};

/**
 * Creates multiple routes with a common base path prefix
 */
export const createRoutes = (
  basePath: string,
  routes: Array<Omit<PluginRoute, 'path'> & { path: string }>
): PluginRoute[] => {
  const normalizedBasePath = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
  
  return routes.map(route => {
    const normalizedPath = route.path.startsWith('/') ? route.path : `/${route.path}`;
    const fullPath = normalizedBasePath + normalizedPath;
    
    const { component, ...routeOptions } = route;
    
    return createRoute(fullPath, component, routeOptions);
  });
};

/**
 * Creates a route configuration for a simple component with automatic naming
 */
export const createSimpleRoute = (
  path: string,
  component: ComponentType<any>,
  title?: string
): PluginRoute => {
  const componentName = component.displayName || component.name || 'UnnamedComponent';
  
  return createRoute(path, component, {
    name: componentName.toLowerCase().replace(/[^a-z0-9]/g, '-'),
    title: title || componentName
  });
};

/**
 * Creates a protected route with a guard function
 */
export const createProtectedRoute = (
  path: string,
  component: ComponentType<any>,
  guard: () => boolean | Promise<boolean>,
  options: Partial<Omit<PluginRoute, 'path' | 'component' | 'guard'>> = {}
): PluginRoute => {
  return createRoute(path, component, {
    ...options,
    guard
  });
};

/**
 * Creates a route with nested children
 */
export const createNestedRoute = (
  path: string,
  component: ComponentType<any>,
  children: PluginRoute[],
  options: Partial<Omit<PluginRoute, 'path' | 'component' | 'children'>> = {}
): PluginRoute => {
  return createRoute(path, component, {
    ...options,
    children
  });
};

/**
 * Helper to create registration options
 */
export const createRegistrationOptions = (
  namespace?: string,
  options: Partial<RouteRegistrationOptions> = {}
): RouteRegistrationOptions => {
  return {
    namespace,
    replace: false,
    priority: 0,
    ...options
  };
};

/**
 * Normalizes a path by ensuring it starts with / and doesn't end with /
 */
export const normalizePath = (path: string): string => {
  if (!path) return '/';
  
  let normalized = path;
  
  // Ensure starts with /
  if (!normalized.startsWith('/')) {
    normalized = '/' + normalized;
  }
  
  // Remove trailing / unless it's the root path
  if (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }
  
  return normalized;
};

/**
 * Combines paths safely
 */
export const combinePaths = (...paths: string[]): string => {
  const cleanPaths = paths
    .filter(Boolean)
    .map(path => path.toString().replace(/^\/+|\/+$/g, ''))
    .filter(Boolean);
  
  return '/' + cleanPaths.join('/');
};